import React from "react";
import { Box, Container, Heading, Text, VStack, Grid, GridItem, Icon, HStack, Badge, Stat, StatLabel, StatNumber, StatHelpText, Divider } from "@chakra-ui/react";
import { FaArrowDown, FaCalculator, FaDollarSign, FaClock, FaCheckCircle, FaChartBar } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const GRAPHITE = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const FinanceServicesRefinance: React.FC = () => {
  const reasons = [
    { icon: FaArrowDown, title: "Lower Interest Rate", desc: "Market rates may have dropped since you took your loan. Save thousands by switching to a better rate.", color: GRAPHITE },
    { icon: FaDollarSign, title: "Access Equity", desc: "Unlock your property's equity for renovations, investments, or consolidating high-interest debt.", color: SAND },
    { icon: FaClock, title: "Better Features", desc: "Upgrade to offset accounts, redraw facilities, and flexible payment options.", color: GRAPHITE },
    { icon: FaChartBar, title: "Debt Consolidation", desc: "Roll high-interest debts into your home loan at a much lower rate.", color: SAND },
    { icon: FaCheckCircle, title: "Fix Your Rate", desc: "Lock in a fixed rate to protect against future rate rises and budget with certainty.", color: GRAPHITE },
    { icon: FaCalculator, title: "Reduce Loan Term", desc: "Same payment, shorter term. Pay off your loan years earlier and save on interest.", color: SAND },
  ];

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/finance/services/refinance"
        fallbackTitle="Refinance Services - One Finance Australia"
        fallbackDescription="Lower your interest rate and save thousands. Compare your current loan against better market options and release equity."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              Save Thousands Per Year
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Refinance Your Home Loan
            </Heading>
            <Text fontSize="lg" maxW="2xl" opacity={0.9}>
              Lower your interest rate, reduce monthly payments, and access your property equity for renovations, investments, or debt consolidation.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Before vs After Comparison */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          See Your Potential Savings
        </Heading>
        <Grid templateColumns={{ base: "1fr", lg: "1fr auto 1fr" }} gap={8} alignItems="center">
          <GridItem>
            <Box p={8} bg="red.50" borderRadius="xl" border="2px solid" borderColor="red.200">
              <Badge colorScheme="red" mb={4}>Current Loan</Badge>
              <VStack spacing={4} align="stretch">
                <Stat>
                  <StatLabel>Interest Rate</StatLabel>
                  <StatNumber color="red.600">5.89%</StatNumber>
                  <StatHelpText>Variable rate</StatHelpText>
                </Stat>
                <Divider />
                <Stat>
                  <StatLabel>Monthly Payment</StatLabel>
                  <StatNumber color="red.600">$3,245</StatNumber>
                  <StatHelpText>Principal & Interest</StatHelpText>
                </Stat>
                <Divider />
                <Stat>
                  <StatLabel>Loan Balance</StatLabel>
                  <StatNumber>$550,000</StatNumber>
                  <StatHelpText>Remaining term: 25 years</StatHelpText>
                </Stat>
              </VStack>
            </Box>
          </GridItem>

          <GridItem>
            <VStack spacing={2}>
              <Icon as={FaArrowDown} boxSize={12} color={GRAPHITE} />
              <Text fontWeight="bold" color={GRAPHITE}>Refinance</Text>
            </VStack>
          </GridItem>

          <GridItem>
            <Box p={8} bg={SAND_LIGHT} borderRadius="xl" border="2px solid" borderColor={SAND}>
              <Badge bg={SAND} color={NAVY} mb={4}>New Loan</Badge>
              <VStack spacing={4} align="stretch">
                <Stat>
                  <StatLabel>Interest Rate</StatLabel>
                  <StatNumber color={GRAPHITE}>4.99%</StatNumber>
                  <StatHelpText>Variable rate</StatHelpText>
                </Stat>
                <Divider />
                <Stat>
                  <StatLabel>Monthly Payment</StatLabel>
                  <StatNumber color={GRAPHITE}>$2,987</StatNumber>
                  <StatHelpText>Principal & Interest</StatHelpText>
                </Stat>
                <Divider />
                <Stat>
                  <StatLabel>Loan Balance</StatLabel>
                  <StatNumber>$550,000</StatNumber>
                  <StatHelpText>Same term: 25 years</StatHelpText>
                </Stat>
              </VStack>
            </Box>
          </GridItem>
        </Grid>

        {/* Savings Breakdown */}
        <Box mt={12} p={10} bg={SAND_LIGHT} borderRadius="2xl" textAlign="center" border="1px solid" borderColor={SAND}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8}>
            <Box>
              <Text fontSize="4xl" fontWeight="bold" color={NAVY}>$258</Text>
              <Text color="gray.700">Per Month</Text>
            </Box>
            <Box>
              <Text fontSize="4xl" fontWeight="bold" color={NAVY}>$3,096</Text>
              <Text color="gray.700">Per Year</Text>
            </Box>
            <Box>
              <Text fontSize="4xl" fontWeight="bold" color={NAVY}>$77,400</Text>
              <Text color="gray.700">Over Loan Life</Text>
            </Box>
          </Grid>
        </Box>
      </Container>

      {/* Reasons to Refinance */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <Heading textAlign="center" mb={12} size="xl">
            Why Refinance?
          </Heading>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={8}>
            {reasons.map((reason, idx) => (
              <Box key={idx} p={6} bg="gray.50" borderRadius="xl" _hover={{ shadow: "lg", transform: "translateY(-4px)" }} transition="all 0.3s">
                <Icon as={reason.icon} boxSize={10} color={reason.color} mb={4} />
                <Heading size="sm" mb={3}>{reason.title}</Heading>
                <Text fontSize="sm" color="gray.600">{reason.desc}</Text>
              </Box>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxW="container.xl" py={16}>
        <Box bg={NAVY} color="white" p={12} borderRadius="2xl" textAlign="center">
          <Icon as={FaCalculator} boxSize={16} mb={6} />
          <Heading size="xl" mb={4}>Calculate Your Refinance Savings</Heading>
          <Text fontSize="lg" mb={8} opacity={0.9}>
            Free comparison and personalised refinance strategy
          </Text>
          <HStack justify="center" spacing={4}>
            <Box
              as="button"
              px={8}
              py={3}
              bg={SAND}
              color={NAVY}
              borderRadius="lg"
              fontWeight="bold"
              _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
            >
              Calculate Savings
            </Box>
            <Box
              as="button"
              px={8}
              py={3}
              bg="transparent"
              border="2px solid"
              borderColor="white"
              borderRadius="lg"
              fontWeight="bold"
              _hover={{ bg: "whiteAlpha.200" }}
            >
              Book Consultation
            </Box>
          </HStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesRefinance;
